import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import axios from '../../resources/axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

interface ScheduledPush {
  _id: string;
  text: string;
  sendAfter: string;
  userType?: string;
  createdAt?: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#404040'}`
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'
  }
}));

const TableHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#FAFAFA' : '#1E1E1E',
  fontWeight: 600,
  fontSize: '14px'
}));

const PREFIX = 'ScheduledPushesTab';

const classes = {
  root: `${PREFIX}-root`,
  emptyState: `${PREFIX}-emptyState`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.emptyState}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    color: theme.palette.text.secondary
  }
}));

const ScheduledPushesTab: React.FC = () => {
  const { t } = useTranslation();
  const [scheduledPushes, setScheduledPushes] = useState<ScheduledPush[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [pushToDelete, setPushToDelete] = useState<ScheduledPush | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchScheduledPushes();
    const interval = setInterval(fetchScheduledPushes, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchScheduledPushes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/notifications/push_notifications/admin', {
        params: { status: 'scheduled', limit: 100 },
      });
      const all = response?.data?.data?.pushNotifications ?? [];
      const now = Date.now();
      const list = all.filter(
        (p: ScheduledPush) => p.sendAfter && new Date(p.sendAfter).getTime() > now
      );
      setScheduledPushes(list);
    } catch (error) {
      console.error('Failed to fetch scheduled pushes:', error);
      setScheduledPushes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (push: ScheduledPush) => {
    setPushToDelete(push);
    setDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!pushToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`/admins/push_notifications/${pushToDelete._id}`);
      setScheduledPushes(scheduledPushes.filter(p => p._id !== pushToDelete._id));
      setDeleteDialog(false);
      setPushToDelete(null);
    } catch (error) {
      console.error('Failed to delete scheduled push:', error);
      alert(t('pushes.errorDeleteScheduled'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog(false);
    setPushToDelete(null);
  };

  const formatTime = (dateString: string) => {
    return moment(dateString).format('DD.MM.YYYY HH:mm:ss');
  };

  const getTimeUntilSend = (sendAfter: string) => {
    const now = moment();
    const sendTime = moment(sendAfter);
    const duration = moment.duration(sendTime.diff(now));

    if (duration.asSeconds() < 0) {
      return t('pushes.immediately');
    }

    if (duration.asDays() >= 1) {
      return t('pushes.timeDaysHours', { days: Math.floor(duration.asDays()), hours: duration.hours() });
    }

    if (duration.asHours() >= 1) {
      return t('pushes.timeHoursMinutes', { hours: Math.floor(duration.asHours()), minutes: duration.minutes() });
    }

    return t('pushes.timeMinutes', { minutes: Math.floor(duration.asMinutes()) });
  };

  if (loading && scheduledPushes.length === 0) {
    return (
      <Root>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  if (scheduledPushes.length === 0) {
    return (
      <Root>
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            {t('pushes.emptyScheduled')}
          </Typography>
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      <Box sx={{ overflowX: 'auto' }}>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('pushes.colText')}</TableHeaderCell>
                <TableHeaderCell>{t('pushes.colUserType')}</TableHeaderCell>
                <TableHeaderCell>{t('pushes.sendTime')}</TableHeaderCell>
                <TableHeaderCell>{t('pushes.colTimeLeft')}</TableHeaderCell>
                <TableHeaderCell align='right'>{t('pushes.colActions')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledPushes.map(push => (
                <StyledTableRow key={push._id}>
                  <StyledTableCell>
                    <Typography variant='body2' sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {push.text}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    {push.userType ? (push.userType === 'vip' ? 'VIP' : t('pushes.normal')) : t('pushes.all')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatTime(push.sendAfter)}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant='body2' sx={{ fontWeight: 500, color: 'warning.main' }}>
                      {getTimeUntilSend(push.sendAfter)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align='right'>
                    <Button
                      size='small'
                      color='error'
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(push)}
                      disabled={deleting}
                    >
                      {t('pushes.cancel')}
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog
        open={deleteDialog}
        onClose={handleCancelDelete}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{t('pushes.cancelScheduledTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mt: 2, mb: 2 }}>
            {t('pushes.cancelScheduledConfirm')}
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant='body2' sx={{ mb: 1 }}>
              <strong>{t('pushes.textColon')}</strong> {pushToDelete?.text}
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              <strong>{t('pushes.sendTimeColon')}</strong> {pushToDelete ? formatTime(pushToDelete.sendAfter) : ''}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            {t('pushes.keepBtn')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : t('pushes.cancelPush')}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default ScheduledPushesTab;
